import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import {
  Card,
  Stack,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Page from "src/components/Page";
import { BackButton } from "src/common/components/BackButton";
import {
  cmCustomerTabList,
  cmWalletHistoryColumns,
  cmTransactionsListColumns
} from "./user.constant";
import { useListWithCompanyId } from "src/hooks/useFetch.hook";
import { FEATURE } from "src/util/enums";
import { useDispatch, useSelector } from "react-redux";
import { useAccess } from "src/hooks/useAccess.hook";
import { TabContext, TabPanel, TabList } from "@mui/lab";
import { RootTabItemStyles } from "src/theme/overrides/Tabs";
import { Context } from "src/context";
import { styled } from "@mui/material/styles";
import { BaseTable } from "../BaseTable/BaseTable";
import CustomerInfoPanel from "./CustomerInfoPanel";
import SentPromoCodesPanel from "./PromoCodesPanel";
import UsedPromoCodesPanel from "./UsedPromoCodesPanel";
import axios from "src/util/axios";
import { cmUserWalletAdjustUrl } from "src/react-query/endPoints";
import { alertSlice } from "src/store/alert/alert.slice";
import InvoiceTemplate from "./InvoiceTemplate";
import { generateTransactionPDFFromHTML } from "./TransactionPdf";
import { useAuth } from "src/hooks/useAuth.hook";
import { useIsGPUser } from "src/hooks/useIsGPUser.hook";
import { walletAdjustmentValidationSchema } from "src/util/validationSchemas";

const RootTabStyle = styled(TabList)(({ theme, length }) => ({
  width: "98%",
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
  "& .MuiTabs-flexContainer": {
    justifyContent: "left",
  },
  "& .MuiTab-root": {
    width: `${100 / length}%`,
  },
}));

const CMCustomerWalletTransactionHistory = () => {
  const { id, companyId } = useParams();
  const { timezone } = useContext(Context);
  const dispatch = useDispatch();
  const { teamId } = useSelector((state) => state.teams);
  const auth = useAuth();
  const isRootUser = useIsGPUser();
  const { data: customers, isFetching, refetch: refetchWalletHistory } = useListWithCompanyId("cmWalletHistory", true, companyId, id);
  const { data: customerDetails, refetch: refetchCustomerDetails } = useListWithCompanyId("cmUserDetails", true, companyId, id);
  const { data: transactions, isFetching: isTransactionFetching } = useListWithCompanyId("cmCustomerTransactionHistory", true, companyId,id);
  const { data: promoCodes } = useListWithCompanyId("getPromoCodes", true, companyId, id);
  const [unUsedPromoCodes, setUnUsedPromoCodes] = useState([]);
  const [usedPromoCodes, setUsedPromoCodes] = useState([]);
  const isCustomerFetching = false;
  const isReadOnly = useAccess(FEATURE.CUSTOMERS, "R");
  const [selectedTab, setSelectedTab] = useState(cmCustomerTabList[0].value);
  const invoiceRef = useRef();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [currentDownloadingId, setCurrentDownloadingId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };
  useEffect(() => {
    setUnUsedPromoCodes(Array.isArray(promoCodes?.sentPromoCodes) && promoCodes?.sentPromoCodes || []);
    setUsedPromoCodes(Array.isArray(promoCodes?.usedPromoCodes) && promoCodes?.usedPromoCodes || []);
  }, [promoCodes]);

  const downloadInvoice = useCallback(async (row) => {
    if (isGeneratingPDF) {
      return; // Prevent multiple clicks
    }

    if (!row?.transactionId) {
      console.error("Missing transactionId while downloading invoice", row);
      return;
    }

    setIsGeneratingPDF(true);
    setCurrentDownloadingId(row?.transactionId);

    // Add a small delay to ensure state update is visible
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const fullUrl = `${process.env.REACT_APP_CSMS_NEST_ENDPOINT}/invoice/send/evdriver`;
      const invoiceData = await axios.post(fullUrl, {
        transactionId: row?.transactionId,
        ...row?.email && { email: row?.email },
        getPdf: true,
      }, {
        headers: { 
          'Content-Type': 'application/json',
        }
      });

      // Try different ways to access the data based on API structure
      const content = invoiceData?.data?.data?.template_content || invoiceData?.data?.template_content;

      if (!content) {
        console.error("No transaction data returned from API");
        return;
      }

      // Keep the original HTML as-is so inline layout/styles are preserved.
      setSelectedTransaction(content);

      try {
        await generateTransactionPDFFromHTML(invoiceRef, row?.transactionId, content);
      } catch (error) {
        console.error("Error generating PDF from HTML:", error);
      } finally {
        setIsGeneratingPDF(false);
        setCurrentDownloadingId(null);
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      setIsGeneratingPDF(false);
      setCurrentDownloadingId(null);
    }
  }, [isGeneratingPDF]);

  // Memoize the columns to ensure proper re-rendering when loading states change
  const transactionColumns = useMemo(() => {
    return cmTransactionsListColumns(isReadOnly, id, downloadInvoice, isGeneratingPDF, currentDownloadingId);
  }, [isReadOnly, id, downloadInvoice, isGeneratingPDF, currentDownloadingId]);

  const getDefaultAdjustFormValues = useCallback(
    () => ({
      adjustmentType: "DEBIT",
      amount: "",
      remarks: "",
      ticketId: "",
      doneByName:
        `${auth?.user?.firstName || ""} ${auth?.user?.lastName || ""}`.trim() ||
        auth?.user?.username ||
        "",
    }),
    [auth]
  );

  const adjustFormik = useFormik({
    enableReinitialize: true,
    initialValues: getDefaultAdjustFormValues(),
    validationSchema: walletAdjustmentValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          adjustmentType: values.adjustmentType,
          amount: Number(values.amount),
          remarks: String(values.remarks).trim(),
          ticketId: String(values.ticketId).trim(),
          doneByName: String(values.doneByName).trim(),
        };

        const response = await axios.post(cmUserWalletAdjustUrl(companyId, id), payload);

        await Promise.all([
          refetchWalletHistory(),
          refetchCustomerDetails(),
        ]);

        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            severity: "success",
            message: response?.data?.message || "Wallet balance adjusted successfully",
          })
        );

        setOpenAdjustDialog(false);
      } catch (error) {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            severity: "error",
            message:
              error?.response?.data?.message ||
              "Failed to adjust wallet balance. Please try again.",
          })
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleOpenAdjustDialog = useCallback(() => {
    adjustFormik.resetForm({ values: getDefaultAdjustFormValues() });
    setOpenAdjustDialog(true);
  }, [adjustFormik, getDefaultAdjustFormValues]);

  const handleCloseAdjustDialog = useCallback(() => {
    if (adjustFormik.isSubmitting) return;
    setOpenAdjustDialog(false);
  }, [adjustFormik.isSubmitting]);

  return (
    <Page title="Customer | Zynka - CMS">
      <BackButton path="/cm-customer" text="Customer Details" />
      <TabContext value={selectedTab}>
        <Stack direction="row" justifyContent="center" alignItem="center" sx={{ width: "100%" }}>
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={cmCustomerTabList.length}
            onChange={handleTabChange}
          >
            {cmCustomerTabList.map((tab) => (
              <RootTabItemStyles key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </RootTabStyle>
        </Stack>
        <Card sx={{ p: 4 }}>
          <TabPanel value="customer_info">
            <CustomerInfoPanel
              customerData={customerDetails?.[0]}
              isLoading={isCustomerFetching}
            />
          </TabPanel>
          <TabPanel value="wallet_history">
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              {isRootUser && (
                <Button
                  variant="contained"
                  onClick={handleOpenAdjustDialog}
                  disabled={isReadOnly}
                >
                  Adjust wallet balance
                </Button>
              )}
            </Box>
            <BaseTable
              rows={customers ?? []}
              columns={cmWalletHistoryColumns(isReadOnly, timezone.timezone?.value)}
              loading={isFetching}
              getRowId={(row) => row.id}
              pageSize={10}
            />
          </TabPanel>
          <TabPanel value="transaction_history">
            <div style={{ position: 'relative' }}>
              {isGeneratingPDF && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '16px 24px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                  }}>
                    <CircularProgress size={24} />
                    <span style={{ fontSize: '14px', color: '#666' }}>Generating PDF...</span>
                  </div>
                </div>
              )}
              <BaseTable
                rows={transactions ?? []}
                columns={transactionColumns}
                loading={isTransactionFetching}
                getRowId={(row) => row.transactionId}
                pageSize={paginationModel.pageSize}
                paginationModel={paginationModel}
                onPageChange={(page) => setPaginationModel(prev => ({ ...prev, page }))}
                onPageSizeChange={(pageSize) => setPaginationModel(prev => ({ ...prev, pageSize, page: 0 }))}
              />
            </div>
          </TabPanel>
          <TabPanel value="sent_promo_codes">
            <SentPromoCodesPanel isReadOnly={isReadOnly} data={unUsedPromoCodes} />
          </TabPanel>
          <TabPanel value="used_promo_codes">
            <UsedPromoCodesPanel isReadOnly={isReadOnly} data={usedPromoCodes} />
          </TabPanel>
        </Card>
      </TabContext>
      {/* Hidden Invoice Template for PDF generation */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0, width: '820px', backgroundColor: '#ffffff' }}>
        <InvoiceTemplate
          ref={invoiceRef}
          transactionData={selectedTransaction}
        />
      </div>

      <Dialog open={openAdjustDialog} onClose={handleCloseAdjustDialog} fullWidth maxWidth="sm">
        <DialogTitle>Adjust wallet balance</DialogTitle>
        <FormikProvider value={adjustFormik}>
          <form onSubmit={adjustFormik.handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Debit or Credit"
                    name="adjustmentType"
                    value={adjustFormik.values.adjustmentType}
                    onChange={adjustFormik.handleChange}
                    onBlur={adjustFormik.handleBlur}
                    error={Boolean(adjustFormik.touched.adjustmentType && adjustFormik.errors.adjustmentType)}
                    helperText={adjustFormik.touched.adjustmentType && adjustFormik.errors.adjustmentType}
                    disabled={adjustFormik.isSubmitting}
                  >
                    <MenuItem value="DEBIT">Debit</MenuItem>
                    <MenuItem value="CREDIT">Credit</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Amount"
                    name="amount"
                    value={adjustFormik.values.amount}
                    onChange={adjustFormik.handleChange}
                    onBlur={adjustFormik.handleBlur}
                    error={Boolean(adjustFormik.touched.amount && adjustFormik.errors.amount)}
                    helperText={adjustFormik.touched.amount && adjustFormik.errors.amount}
                    disabled={adjustFormik.isSubmitting}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ticket ID (ZynkaOps)"
                    name="ticketId"
                    value={adjustFormik.values.ticketId}
                    onChange={adjustFormik.handleChange}
                    onBlur={adjustFormik.handleBlur}
                    error={Boolean(adjustFormik.touched.ticketId && adjustFormik.errors.ticketId)}
                    helperText={adjustFormik.touched.ticketId && adjustFormik.errors.ticketId}
                    disabled={adjustFormik.isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Remarks"
                    name="remarks"
                    value={adjustFormik.values.remarks}
                    onChange={adjustFormik.handleChange}
                    onBlur={adjustFormik.handleBlur}
                    error={Boolean(adjustFormik.touched.remarks && adjustFormik.errors.remarks)}
                    helperText={adjustFormik.touched.remarks && adjustFormik.errors.remarks}
                    disabled={adjustFormik.isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Done By"
                    name="doneByName"
                    value={adjustFormik.values.doneByName}
                    onChange={adjustFormik.handleChange}
                    onBlur={adjustFormik.handleBlur}
                    error={Boolean(adjustFormik.touched.doneByName && adjustFormik.errors.doneByName)}
                    helperText={adjustFormik.touched.doneByName && adjustFormik.errors.doneByName}
                    disabled={adjustFormik.isSubmitting}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdjustDialog} disabled={adjustFormik.isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={adjustFormik.isSubmitting}
              >
                {adjustFormik.isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </DialogActions>
          </form>
        </FormikProvider>
      </Dialog>
    </Page>
  );
};

export default CMCustomerWalletTransactionHistory;
